import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  Tooltip,
  Collapse
} from '@mui/material';
import { Icon } from '@iconify/react';
import addFill from '@iconify/icons-eva/plus-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import filterFill from '@iconify/icons-eva/funnel-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';

// Filter criteria structure
const createEmptyFilter = () => ({
  id: Date.now() + Math.random(),
  field: '',
  operator: '',
  value: '',
  valueType: 'string'
});

// Available operators by field type
const getOperatorsForField = (field) => {
  const numberFields = ["kw_consumed", "totalAmountPaid", "totalAmountCharged", "totalRefund"];
  const dateFields = ["starttime", "stoptime"];
  const stringFields = ["transactionId", "paymentId", "email", "phone", "chargepoint_id", "connectorId", "payment_type", "notes"];

  if (numberFields.includes(field)) {
    return [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'greaterThan', label: 'Greater Than' },
      { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
      { value: 'lessThan', label: 'Less Than' },
      { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
      { value: 'between', label: 'Between' },
      { value: 'isEmpty', label: 'Is Empty' },
      { value: 'isNotEmpty', label: 'Is Not Empty' }
    ];
  }

  if (dateFields.includes(field)) {
    return [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'after', label: 'After' },
      { value: 'onOrAfter', label: 'On or After' },
      { value: 'before', label: 'Before' },
      { value: 'onOrBefore', label: 'On or Before' },
      { value: 'between', label: 'Between' },
      { value: 'isEmpty', label: 'Is Empty' },
      { value: 'isNotEmpty', label: 'Is Not Empty' }
    ];
  }

  // String fields
  return [
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does Not Contain' },
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Not Equals' },
    { value: 'startsWith', label: 'Starts With' },
    { value: 'endsWith', label: 'Ends With' },
    { value: 'isEmpty', label: 'Is Empty' },
    { value: 'isNotEmpty', label: 'Is Not Empty' },
    { value: 'isAnyOf', label: 'Is Any Of' }
  ];
};

// Field options
const fieldOptions = [
  { value: 'transactionId', label: 'Transaction ID', type: 'string' },
  { value: 'paymentId', label: 'Payment ID', type: 'string' },
  { value: 'email', label: 'Email', type: 'string' },
  { value: 'phone', label: 'Phone', type: 'string' },
  { value: 'chargepoint_id', label: 'ChargeBox ID', type: 'string' },
  { value: 'connectorId', label: 'Connector', type: 'string' },
  { value: 'payment_type', label: 'Payment Type', type: 'string' },
  { value: 'kw_consumed', label: 'kWh Consumed', type: 'number' },
  { value: 'totalAmountPaid', label: 'Amount Paid', type: 'number' },
  { value: 'totalAmountCharged', label: 'Charging Cost', type: 'number' },
  { value: 'totalRefund', label: 'Refund / Wallet Balance', type: 'number' },
  { value: 'starttime', label: 'Start Time', type: 'date' },
  { value: 'stoptime', label: 'Stop Time', type: 'date' },
  { value: 'notes', label: 'Notes', type: 'string' }
];

export const CompactFilter = ({ 
  filters = [], 
  onFiltersChange, 
  onApplyFilters, 
  onClearFilters,
  searchText = "",
  onSearchChange,
  onExpandFilter
}) => {
  const [localFilters, setLocalFilters] = useState(filters.length > 0 ? filters : [createEmptyFilter()]);
  const [logicalOperator, setLogicalOperator] = useState('AND');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleAddFilter = () => {
    const newFilter = createEmptyFilter();
    setLocalFilters(prev => [...prev, newFilter]);
    // Auto-collapse transaction overview when adding a filter
    if (onExpandFilter) {
      onExpandFilter();
    }
  };

  const handleRemoveFilter = (filterId) => {
    const newFilters = localFilters.filter(filter => filter.id !== filterId);
    setLocalFilters(newFilters);
    
    // Notify parent component about the filter change
    const validFilters = newFilters.filter(filter => 
      filter.field && filter.operator && 
      (filter.value || ['isEmpty', 'isNotEmpty'].includes(filter.operator))
    );
    onFiltersChange(validFilters, logicalOperator);
  };

  const handleFilterChange = (filterId, field, value) => {
    setLocalFilters(prev => prev.map(filter => {
      if (filter.id === filterId) {
        const updatedFilter = { ...filter, [field]: value };
        
        // Reset operator and value when field changes
        if (field === 'field') {
          updatedFilter.operator = '';
          updatedFilter.value = '';
          updatedFilter.valueType = fieldOptions.find(f => f.value === value)?.type || 'string';
        }
        
        // Reset value when operator changes
        if (field === 'operator') {
          updatedFilter.value = '';
        }
        
        return updatedFilter;
      }
      return filter;
    }));
  };

  const handleApplyFilters = () => {
    const validFilters = localFilters.filter(filter => 
      filter.field && filter.operator && 
      (filter.value || ['isEmpty', 'isNotEmpty'].includes(filter.operator))
    );
    
    onFiltersChange(validFilters, logicalOperator);
    onApplyFilters();
  };

  const handleClearFilters = () => {
    setLocalFilters([createEmptyFilter()]);
    setLogicalOperator('AND');
    onClearFilters();
    // Clear search text as well
    onSearchChange("");
    // Notify parent component about the filter change
    onFiltersChange([], 'AND');
  };

  const renderValueInput = (filter) => {
    const { operator, value, valueType } = filter;
    
    if (['isEmpty', 'isNotEmpty'].includes(operator)) {
      return (
        <Box sx={{ 
          p: 2, 
          bgcolor: 'info.50', 
          borderRadius: 1, 
          border: '1px solid',
          borderColor: 'info.200',
          textAlign: 'center'
        }}>
          <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
            {operator === 'isEmpty' ? '🔍 Will find empty values' : '🔍 Will find non-empty values'}
          </Typography>
        </Box>
      );
    }

    if (operator === 'between') {
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={valueType === 'date' ? 'Start date' : valueType === 'number' ? 'Min value' : 'From'}
            value={Array.isArray(value) ? value[0] || '' : ''}
            onChange={(e) => {
              const newValue = Array.isArray(value) ? [e.target.value, value[1]] : [e.target.value, ''];
              handleFilterChange(filter.id, 'value', newValue);
            }}
            type={valueType === 'number' ? 'number' : valueType === 'date' ? 'date' : 'text'}
            sx={{ 
              width: 120,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white'
              }
            }}
            InputProps={{
              startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>From:</Typography>
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>to</Typography>
          <TextField
            size="small"
            placeholder={valueType === 'date' ? 'End date' : valueType === 'number' ? 'Max value' : 'To'}
            value={Array.isArray(value) ? value[1] || '' : ''}
            onChange={(e) => {
              const newValue = Array.isArray(value) ? [value[0], e.target.value] : ['', e.target.value];
              handleFilterChange(filter.id, 'value', newValue);
            }}
            type={valueType === 'number' ? 'number' : valueType === 'date' ? 'date' : 'text'}
            sx={{ 
              width: 120,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white'
              }
            }}
            InputProps={{
              startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>To:</Typography>
            }}
          />
        </Box>
      );
    }

    if (operator === 'isAnyOf') {
      return (
        <TextField
          size="small"
          placeholder="e.g., UPI, Card, Wallet (separate with commas)"
          value={Array.isArray(value) ? value.join(', ') : value || ''}
          onChange={(e) => {
            const values = e.target.value.split(',').map(v => v.trim()).filter(v => v);
            handleFilterChange(filter.id, 'value', values);
          }}
          sx={{ 
            minWidth: 250,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white'
            }
          }}
          InputProps={{
            startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>Values:</Typography>
          }}
        />
      );
    }

    const getPlaceholder = () => {
      if (valueType === 'date') return 'Select date (YYYY-MM-DD)';
      if (valueType === 'number') return 'Enter number (e.g., 100.50)';
      return 'Enter text value';
    };

    return (
      <TextField
        size="small"
        placeholder={getPlaceholder()}
        value={value || ''}
        onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
        type={valueType === 'number' ? 'number' : valueType === 'date' ? 'date' : 'text'}
        sx={{ 
          minWidth: 200,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'white'
          }
        }}
        InputProps={{
          startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>Value:</Typography>
        }}
      />
    );
  };

  // Format applied filters for display
  const formatAppliedFilter = (filter) => {
    const fieldLabel = fieldOptions.find(f => f.value === filter.field)?.label || filter.field;
    const operatorLabel = getOperatorsForField(filter.field).find(op => op.value === filter.operator)?.label || filter.operator;
    
    let valueDisplay = '';
    if (['isEmpty', 'isNotEmpty'].includes(filter.operator)) {
      valueDisplay = '';
    } else if (filter.operator === 'between' && Array.isArray(filter.value)) {
      valueDisplay = ` ${filter.value[0] || ''} - ${filter.value[1] || ''}`;
    } else if (filter.operator === 'isAnyOf' && Array.isArray(filter.value)) {
      valueDisplay = ` ${filter.value.join(', ')}`;
    } else {
      valueDisplay = ` "${filter.value}"`;
    }
    
    return `${fieldLabel} ${operatorLabel}${valueDisplay}`;
  };

  const appliedFilters = localFilters
    .filter(filter => filter.field && filter.operator && 
      (filter.value || ['isEmpty', 'isNotEmpty'].includes(filter.operator)))
    .map(formatAppliedFilter);

  const activeFiltersCount = appliedFilters.length;

  return (
    <Box sx={{ mb: 2 }}>
             {/* Search and Filter Controls */}
             <Box sx={{ 
               display: 'flex', 
               alignItems: 'center', 
               gap: 1.5, 
               mb: 1.5,
               p: 1.5,
               bgcolor: 'grey.50',
               borderRadius: 1,
               border: '1px solid',
               borderColor: 'grey.200'
             }}>
        <Tooltip title="Add advanced filters to narrow down your search results">
          <Button
            variant={isExpanded ? "contained" : "outlined"}
            startIcon={<Icon icon={addFill} />}
            onClick={() => {
              const newExpandedState = !isExpanded;
              setIsExpanded(newExpandedState);
              // Auto-collapse transaction overview when expanding filters
              if (newExpandedState && onExpandFilter) {
                onExpandFilter();
              }
            }}
            size="small"
            sx={{ 
              minWidth: 120,
              fontWeight: 600,
              ...(isExpanded && {
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' }
              })
            }}
          >
            {isExpanded ? 'Hide Filters' : 'Add Filter'}
          </Button>
        </Tooltip>
        
        <Box sx={{ flex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="🔍 Search transactions by ID, payment ID, email, phone, charger, or payment type..."
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <Icon icon={filterFill} style={{ marginRight: 8, color: '#1976d2' }} />
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }
            }}
          />
        </Box>
        
        {activeFiltersCount > 0 && (
          <Tooltip title="Remove all applied filters">
            <Button
              variant="outlined"
              startIcon={<Icon icon={refreshFill} />}
              onClick={handleClearFilters}
              size="small"
              color="error"
              sx={{ 
                minWidth: 100,
                fontWeight: 600,
                borderColor: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  bgcolor: 'error.light',
                  color: 'error.dark'
                }
              }}
            >
              Clear All
            </Button>
          </Tooltip>
        )}
      </Box>

             {/* Applied Filters Display */}
             {appliedFilters.length > 0 && (
               <Box sx={{ 
                 mb: 1.5, 
                 p: 1.5, 
                 bgcolor: 'primary.50', 
                 borderRadius: 1, 
                 border: '1px solid',
                 borderColor: 'primary.200'
               }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                   <Icon icon={filterFill} width={14} height={14} color="#1976d2" />
                   <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                     Active Filters ({appliedFilters.length}):
                   </Typography>
                 </Box>
                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                   {appliedFilters.map((filterText, index) => (
                     <Chip
                       key={index}
                       label={filterText}
                       size="small"
                       color="primary"
                       variant="filled"
                       onDelete={() => handleRemoveFilter(localFilters[index].id)}
                       sx={{ 
                         fontSize: '0.7rem',
                         fontWeight: 500,
                         height: 20,
                         '& .MuiChip-deleteIcon': {
                           color: 'white',
                           fontSize: '0.8rem',
                           '&:hover': {
                             color: 'primary.light'
                           }
                         }
                       }}
                     />
                   ))}
                 </Box>
               </Box>
             )}

             {/* Collapsible Filter Form */}
             <Collapse in={isExpanded}>
               <Box sx={{ 
                 border: '1px solid', 
                 borderColor: 'primary.200', 
                 borderRadius: 1, 
                 p: 2,
                 bgcolor: 'white',
                 boxShadow: 1
               }}>
                 <Box sx={{ 
                   display: 'flex', 
                   alignItems: 'center', 
                   gap: 2, 
                   mb: 2,
                   p: 1.5,
                   bgcolor: 'grey.50',
                   borderRadius: 1,
                   border: '1px solid',
                   borderColor: 'grey.200'
                 }}>
                   <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                     🔧 Logic:
                   </Typography>
                   <FormControl size="small" sx={{ minWidth: 120 }}>
                     <InputLabel>Combine filters</InputLabel>
                     <Select
                       value={logicalOperator}
                       onChange={(e) => setLogicalOperator(e.target.value)}
                       label="Combine filters"
                     >
                       <MenuItem value="AND">
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Typography variant="body2">All match (AND)</Typography>
                           <Chip label="Strict" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                         </Box>
                       </MenuItem>
                       <MenuItem value="OR">
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Typography variant="body2">Any match (OR)</Typography>
                           <Chip label="Flexible" size="small" color="success" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }} />
                         </Box>
                       </MenuItem>
                     </Select>
                   </FormControl>
                   <Tooltip title="AND: All filters must be true. OR: At least one filter must be true.">
                     <Icon icon={filterFill} style={{ color: '#666', cursor: 'help' }} />
                   </Tooltip>
                 </Box>

          {localFilters.map((filter, index) => (
            <Box key={filter.id} sx={{ 
              mb: 2, 
              p: 1.5, 
              border: '1px solid', 
              borderColor: 'grey.300', 
              borderRadius: 1,
              bgcolor: 'grey.25'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Filter #{index + 1}:
                </Typography>
                {index < localFilters.length - 1 && (
                  <Chip 
                    label={logicalOperator} 
                    size="small" 
                    variant="filled"
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: '0.6rem', height: 18 }}
                  />
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>📋 Field</InputLabel>
                  <Select
                    value={filter.field}
                    onChange={(e) => handleFilterChange(filter.id, 'field', e.target.value)}
                    label="📋 Field"
                  >
                    {fieldOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{option.label}</Typography>
                          <Chip 
                            label={option.type} 
                            size="small" 
                            color={option.type === 'number' ? 'warning' : option.type === 'date' ? 'info' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.5rem', height: 14 }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>⚡ Operator</InputLabel>
                  <Select
                    value={filter.operator}
                    onChange={(e) => handleFilterChange(filter.id, 'operator', e.target.value)}
                    label="⚡ Operator"
                    disabled={!filter.field}
                  >
                    {getOperatorsForField(filter.field).map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography variant="body2">{option.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ flex: 1, minWidth: 180 }}>
                  {renderValueInput(filter)}
                </Box>

                <Tooltip title="Remove this filter">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFilter(filter.id)}
                    disabled={localFilters.length === 1}
                    sx={{ 
                      color: 'error.main',
                      '&:hover': { 
                        bgcolor: 'error.light',
                        color: 'error.dark'
                      }
                    }}
                  >
                    <Icon icon={closeFill} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}

                 <Box sx={{ 
                   display: 'flex', 
                   gap: 1.5, 
                   justifyContent: 'space-between', 
                   alignItems: 'center',
                   mt: 2,
                   pt: 1.5,
                   borderTop: '1px solid',
                   borderColor: 'grey.300'
                 }}>
                   <Tooltip title="Add another filter condition">
                     <Button
                       startIcon={<Icon icon={addFill} />}
                       onClick={handleAddFilter}
                       variant="outlined"
                       size="small"
                       sx={{ 
                         fontWeight: 600,
                         borderColor: 'primary.main',
                         color: 'primary.main',
                         fontSize: '0.75rem',
                         py: 0.5,
                         '&:hover': {
                           borderColor: 'primary.dark',
                           bgcolor: 'primary.light',
                           color: 'primary.dark'
                         }
                       }}
                     >
                       Add Filter
                     </Button>
                   </Tooltip>

                   <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                     <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                       {activeFiltersCount > 0 ? `${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} ready` : 'No filters applied'}
                     </Typography>
                     <Button
                       onClick={handleApplyFilters}
                       variant="contained"
                       size="small"
                       disabled={activeFiltersCount === 0}
                       sx={{ 
                         fontWeight: 600,
                         minWidth: 100,
                         fontSize: '0.75rem',
                         py: 0.5,
                         bgcolor: activeFiltersCount > 0 ? 'success.main' : 'grey.400',
                         '&:hover': {
                           bgcolor: activeFiltersCount > 0 ? 'success.dark' : 'grey.500'
                         }
                       }}
                     >
                       ✅ Apply
                     </Button>
                   </Box>
                 </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
